import React from "react";
import "./gameStageLayout.css";

/**
 * Shared India-travel stage shell: timer, points/skip bar,
 * decorative region banner, centered instructions, and content frame.
 */
export default function GameStageLayout({
  title,
  subtitle,
  secondSubtitle,
  themedTextStyle,
  elapsedClock,
  points,
  onHelp,
  onSkip,
  submitting = false,
  accent = "orange",
  className = "",
  children,
  footer = null,
}) {
  const pointsLabel =
    typeof points === "number"
      ? String(Math.max(0, Math.floor(points))).padStart(2, "0")
      : String(points ?? "0");

  return (
    <div
      className={`game-stage game-stage--accent-${accent}${className ? ` ${className}` : ""}`}
    >
      <div className="game-stage__shell">
        <div className="game-stage__main">
          <div className="game-stage__toolbar">
            <div className="game-stage__timer" aria-label={`Time ${elapsedClock}`}>
              <span className="game-stage__timer-icon" aria-hidden>
                <svg viewBox="0 0 40 44" width="36" height="40">
                  <rect x="8" y="2" width="24" height="5" rx="1.5" fill="#3a3a3a" />
                  <rect x="8" y="37" width="24" height="5" rx="1.5" fill="#3a3a3a" />
                  <path
                    d="M11 7h18l-7.5 13.5L29 34H11l7.5-13.5L11 7z"
                    fill="#fff"
                    stroke="#3a3a3a"
                    strokeWidth="1.4"
                    strokeLinejoin="round"
                  />
                  <path d="M14 9.5h12l-4.2 7.6H18.2L14 9.5z" fill="url(#gsHourSandTop)" />
                  <path d="M18.2 25.8h3.6L26 32.5H14l4.2-6.7z" fill="url(#gsHourSandBot)" />
                  <circle cx="20" cy="21" r="1.1" fill="#f5c842" />
                  <circle cx="18.6" cy="23.2" r="0.8" fill="#f0a020" />
                  <circle cx="21.4" cy="23.8" r="0.7" fill="#f5c842" />
                  <defs>
                    <linearGradient id="gsHourSandTop" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ffe27a" />
                      <stop offset="100%" stopColor="#f0a020" />
                    </linearGradient>
                    <linearGradient id="gsHourSandBot" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f5c842" />
                      <stop offset="100%" stopColor="#e08a10" />
                    </linearGradient>
                  </defs>
                </svg>
              </span>
              <span className="game-stage__timer-value">{elapsedClock}</span>
            </div>

            {title ? (
              <h1 className="game-stage__banner game-stage__banner--inline">
                <span className="game-stage__banner-text">{title}</span>
              </h1>
            ) : null}

            <div className="game-stage__top-actions">
              <div className="game-stage__points" aria-label={`Points ${pointsLabel}`}>
                <span className="game-stage__points-star" aria-hidden>
                  <svg viewBox="0 0 48 48" width="40" height="40">
                    <path
                      d="M24 4.5l5.4 11 12.1 1.8-8.8 8.5 2.1 12.1L24 32.4 13.2 37.9l2.1-12.1-8.8-8.5 12.1-1.8L24 4.5z"
                      fill="#c47810"
                      transform="translate(0 2.5)"
                    />
                    <path
                      d="M24 4.5l5.4 11 12.1 1.8-8.8 8.5 2.1 12.1L24 32.4 13.2 37.9l2.1-12.1-8.8-8.5 12.1-1.8L24 4.5z"
                      fill="url(#gsStarFill)"
                      stroke="#e8a010"
                      strokeWidth="0.8"
                    />
                    <defs>
                      <linearGradient id="gsStarFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#fff3a0" />
                        <stop offset="45%" stopColor="#ffd54a" />
                        <stop offset="100%" stopColor="#f0a020" />
                      </linearGradient>
                    </defs>
                  </svg>
                </span>
                <span className="game-stage__points-value">{pointsLabel}</span>
              </div>
              {onHelp ? (
                <button
                  type="button"
                  className="game-stage__help-btn"
                  aria-label="How to play"
                  onClick={onHelp}
                >
                  ?
                </button>
              ) : null}
              <button
                type="button"
                className="game-stage__skip-btn"
                disabled={submitting}
                onClick={onSkip}
              >
                Skip
                <span className="game-stage__skip-icon" aria-hidden />
              </button>
            </div>
          </div>

          <div className="game-stage__frame">
            {subtitle ? (
              <p className="game-stage__instructions" style={themedTextStyle}>
                {subtitle}
              </p>
            ) : null}
            {secondSubtitle ? (
              <p
                className="game-stage__instructions game-stage__instructions--secondary"
                style={themedTextStyle}
              >
                {secondSubtitle}
              </p>
            ) : null}

            <div className="game-stage__content">{children}</div>

            {footer ? <div className="game-stage__footer">{footer}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
